/**
 * Theme (§12.4): `data-theme` on `<html>`, dark by default, persisted on the user record and
 * mirrored to localStorage.
 *
 * localStorage is what makes the first paint correct — waiting for `GET /v1/me` would flash
 * the dark shell at a user who chose light.
 */
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { api } from '@/api/client'

export type Theme = 'system' | 'dark' | 'light'

const STORAGE_KEY = 'nlocal_theme'

function readStored(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'dark' || stored === 'light' || stored === 'system') return stored
  } catch {
    // Private browsing, or site data blocked. Fall through to the default.
  }
  return 'dark'
}

function systemPrefersDark(): boolean {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? true
}

export const useThemeStore = defineStore('theme', () => {
  const theme = ref<Theme>(readStored())

  function apply(): void {
    const root = document.documentElement
    const dark = theme.value === 'dark' || (theme.value === 'system' && systemPrefersDark())

    // Both layers move together: Tailwind's darkMode:'class' keys off `dark`, and the
    // semantic token layer keys off `data-theme`. Setting only one leaves the two palettes
    // disagreeing (see packages/ui/TOKENS.md).
    root.classList.toggle('dark', dark)
    if (dark) root.removeAttribute('data-theme')
    else root.setAttribute('data-theme', 'light')
  }

  function set(next: Theme, persist = true): void {
    theme.value = next

    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Non-fatal: the theme still applies for this page load.
    }

    if (persist) {
      void api.PATCH('/v1/me', { body: { theme: next } })
    }
  }

  function toggle(): void {
    set(theme.value === 'light' ? 'dark' : 'light')
  }

  watch(theme, apply, { immediate: true })

  // Follow the OS while the user is on "system".
  window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (theme.value === 'system') apply()
  })

  return { theme, set, toggle, apply }
})
